import { ref } from "vue";
import { useAuth } from "./useAuth";
import { useJobs } from "./useJobs";
import { readUserFacingError } from "../errorMessages";

/**
 * Composable for the delete-job action. Wraps the DELETE /api/jobs/:id
 * call plus the side effects:
 *  - On 204: remove the row from the module-level useJobs cache so the
 *    HomeView list updates without a fresh fetch.
 *  - On 401: clear local auth (matches every other api call's behavior;
 *    triggers App.vue's SIGNED_OUT_EVENT listener to route to /login).
 *  - On other non-2xx: leave state alone, return an error message so
 *    the UI can toast it.
 *
 * Module-level `inFlight` ref so multiple ConfirmDialogs sharing the
 * same composable don't double-fire (currently only one dialog can
 * exist at a time, but the lock is cheap and future-proofs).
 */

const inFlight = ref(false);

/**
 * Discriminated union — `ok: true` carries no error; `ok: false`
 * REQUIRES `error: string`. Callers that destructure this get a
 * compile-time guarantee that the failure path has a message to
 * surface, eliminating a class of bugs where a future branch
 * forgets to set `error` and silently swallows the failure.
 * (Caught by type-design-analyzer in the PR #810 review pass.)
 */
export type DeleteResult =
  | { ok: true }
  | { ok: false; error: string };

export function useDeleteJob() {
  async function deleteJob(jobId: string): Promise<DeleteResult> {
    if (inFlight.value) {
      // Guard against accidental double-submit via repeated click.
      return { ok: false, error: "A delete is already in progress." };
    }
    inFlight.value = true;
    try {
      const resp = await fetch(`/api/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (resp.status === 204) {
        // Prune from the cached list so HomeView reflects the delete
        // without a fresh GET round-trip.
        const { jobs } = useJobs();
        jobs.value = jobs.value.filter((j) => j.job_id !== jobId);
        return { ok: true };
      }
      if (resp.status === 401) {
        // Mirrors useJobs.apiFetch: 401 means the session is gone
        // server-side. Clear local auth so App.vue routes to /login.
        const { signOut } = useAuth();
        await signOut();
        return { ok: false, error: "Session expired." };
      }
      if (resp.status === 404) {
        // Either someone else's job (existence leak guard returns 404)
        // OR a row that was already deleted (e.g., from another tab).
        // Prune locally anyway — the row is gone from the server's
        // perspective, so the list should match.
        const { jobs } = useJobs();
        jobs.value = jobs.value.filter((j) => j.job_id !== jobId);
        return { ok: false, error: "Job no longer exists." };
      }
      // C-S8 (sync-channel separation): render USER_MESSAGES[code]
      // for Problem Details bodies, generic-by-status otherwise.
      // NEVER the raw response body. See
      // docs/design/sync-channel-leak-fix-one-pager.md.
      // Minimal migration after the readUserFacingError widening to
      // ``{ message, traceId? }``: take ``.message``. Rendering the
      // ``Reference:`` line in HomeView's delete-toast is a follow-up
      // (not in error-correlation-one-pager.md §A.4 caller matrix).
      return { ok: false, error: (await readUserFacingError(resp)).message };
    } catch (err) {
      return {
        ok: false,
        error: `Cannot reach backend: ${(err as Error).message}`,
      };
    } finally {
      inFlight.value = false;
    }
  }

  return {
    deleteJob,
    inFlight,
    /** Test-only. */
    _resetForTests() {
      inFlight.value = false;
    },
  };
}
